// A KPI's months as a small line chart, drawn as inline SVG (no chart library): one neutral line, a dot per month,
// the latest value labelled above its end and the months beneath, with no gridlines. A month with no figure breaks
// the line rather than drawing it as 0. The picture is hidden from screen readers, which read the same months
// from a visually hidden table instead.
import type { Month } from "../api.js";
import { useLanguage, useWords } from "../i18n/words.js";
import { monthAndYear, monthName, valueWords } from "./words.js";

/** The theme tokens the chart is drawn in: graphite neutrals only, never amber. */
export const TREND_INK = { line: "ink-2", months: "ink-3", value: "ink" } as const;

const WIDTH = 320;
const HEIGHT = 130;
const LEFT = 12;
const RIGHT = 16;
/** Room above the line for the latest value, which is right-aligned to the chart's edge: a long one such as
 * "-3.1 percentage points" grows leftwards into the chart, never out of it and off a phone's screen (#347). */
const TOP = 34;
const VALUE_BASELINE = 18;
const BOTTOM = 26;
/** The most month labels that fit under the line; with more, every other one is left out, counting back from the latest. */
const LABELS_FIT = 7;

const ink = (token: string) => `var(--${token})`;

export interface TrendPoint {
  readonly month: Month;
  readonly value: number | null;
}

/** Where each month sits: x across the months, y from the lowest figure to the highest (the middle when all equal). */
export function place(values: readonly TrendPoint[]): { x: number; y: number | null }[] {
  const figures = values.flatMap((v) => (v.value === null ? [] : [v.value]));
  const [low, high] = [Math.min(...figures), Math.max(...figures)];
  const across = WIDTH - LEFT - RIGHT;
  const down = HEIGHT - TOP - BOTTOM;
  return values.map((v, i) => ({
    x: LEFT + (values.length === 1 ? across / 2 : (across * i) / (values.length - 1)),
    y: v.value === null ? null : high === low ? TOP + down / 2 : TOP + (down * (high - v.value)) / (high - low),
  }));
}

/** The line's runs of consecutive months with figures, each as SVG path data. */
export function segments(points: readonly { x: number; y: number | null }[]): string[] {
  const runs: { x: number; y: number }[][] = [[]];
  for (const { x, y } of points) {
    if (y === null) runs.push([]);
    else runs.at(-1)?.push({ x, y });
  }
  return runs
    .filter((run) => run.length > 1)
    .map((run) => run.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" "));
}

export function Trend({ name, unit, values }: { name: string; unit: string; values: readonly TrendPoint[] }) {
  const w = useWords();
  const { language } = useLanguage();
  const points = place(values);
  const last = values.findLastIndex((v) => v.value !== null);
  const end = points[last];
  const endValue = values[last]?.value;
  const labelled = (i: number) => values.length <= LABELS_FIT || (values.length - 1 - i) % 2 === 0;
  return (
    <figure className="trend">
      <svg viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`} aria-hidden="true" focusable="false">
        {segments(points).map((d) => (
          <path key={d} d={d} fill="none" stroke={ink(TREND_INK.line)} strokeWidth={2.5} strokeLinejoin="round" />
        ))}
        {points.map((p, i) =>
          p.y === null ? null : (
            <circle key={values[i]?.month} cx={p.x} cy={p.y} r={i === last ? 4 : 2.5} fill={ink(TREND_INK.line)} />
          ),
        )}
        {end && end.y !== null && endValue !== undefined && endValue !== null ? (
          <text x={WIDTH} y={VALUE_BASELINE} textAnchor="end" fill={ink(TREND_INK.value)} className="trend-value">
            {valueWords(endValue, unit, language)}
          </text>
        ) : null}
        {points.map((p, i) =>
          labelled(i) ? (
            <text
              key={values[i]?.month}
              x={p.x}
              y={HEIGHT - 6}
              textAnchor="middle"
              fill={ink(TREND_INK.months)}
              className="trend-month"
            >
              {monthName(values[i]?.month ?? "", "short", language)}
            </text>
          ) : null,
        )}
      </svg>
      {/* Hidden by a box: a table never shrinks to the 1px of `sr-only`, and would widen a phone's page. */}
      <div className="sr-only">
        <table>
          <caption>{w.trendCaption(name)}</caption>
          <thead>
            <tr>
              <th scope="col">{w.trendMonth}</th>
              <th scope="col">{name}</th>
            </tr>
          </thead>
          <tbody>
            {values.map((v) => (
              <tr key={v.month}>
                <th scope="row">{monthAndYear(v.month, language)}</th>
                <td>{v.value === null ? w.trendNoFigure : valueWords(v.value, unit, language)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
